import { ScrollView } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { sizes } from "@src/constants/sizes";
import { SupportAccessRestricted } from "@src/components/web/SupportAccessRestricted.web";
import { FullScreenLoading } from "@src/components/screen-state";
import { universalConfirm } from "@src/components/UniversalAlert";
import { showErrorToast, showSuccessToast } from "@src/utils/toast";
import { useSupportUserStatus } from "@src/features/tenants";
import { useBooleanFlag, FEATURE_FLAGS } from "@src/services/feature-flags/feature-flags";
import { useTriggerTestSignal } from "@visioni/a3s-monitoring/app";
import type { TestSignalPath } from "@visioni/a3s-monitoring/app";
import { resolveTestSignalsAccess } from "../access";
import { TEST_SIGNAL_ACTIONS } from "../actions";
import { useTestSignalCooldown } from "../useTestSignalCooldown";
import { TestSignalActionCard } from "./TestSignalActionCard";

interface CameraTestSignalsProps {
  siteId: string;
  cameraKey: string;
}

/**
 * Support-only per-camera test signal triggers.
 *
 * Deliberately fire-and-forget: the API can only report that the command was
 * accepted for delivery, so the notice tells the operator where to look for
 * the outcome rather than the page pretending to know it.
 */
export function CameraTestSignals({ siteId, cameraKey }: CameraTestSignalsProps) {
  const { theme } = useUnistyles();
  const supportStatus = useSupportUserStatus();
  const uiFlagEnabled = useBooleanFlag(FEATURE_FLAGS.TEST_SIGNAL_TRIGGER_UI);
  const trigger = useTriggerTestSignal();
  const cooldown = useTestSignalCooldown();

  const access = resolveTestSignalsAccess(supportStatus, uiFlagEnabled);

  if (access === "pending") {
    return <FullScreenLoading message="Checking access..." />;
  }
  if (access === "denied") {
    return <SupportAccessRestricted />;
  }

  const fire = async (signalPath: TestSignalPath, title: string, confirmMessage: string) => {
    if (!(await universalConfirm({ title, message: confirmMessage, confirmLabel: "Send" }))) return;

    try {
      await trigger.mutateAsync({ siteId, cameraKey, signalPath });
      cooldown.markFired(signalPath);
      // Deliberately not "test signal fired": all that is known here is that
      // the command was accepted for delivery to the device.
      showSuccessToast({ message: "Command sent to the camera" });
    } catch (error: unknown) {
      showErrorToast({ error });
    }
  };

  return (
    <ScrollView
      contentContainerStyle={{
        padding: theme.spacing["3xl"],
        gap: theme.spacing["3xl"],
        // Content stays a readable column and centres in the leftover space,
        // matching the camera detail page rather than stretching to the window.
        alignItems: "center",
      }}
    >
      <View width="100%" maxWidth={sizes.webContentMaxWidth} gap="3xl">
        <View
          bg="surfaceCardInner"
          borderRadius={theme.spacing.lg}
          borderWidth={1}
          borderColor="borderCard"
          p="3xl"
          gap="md"
        >
          <Text textStyle="sm/semibold" color="text">
            These buttons send a command only
          </Text>
          <Text textStyle="sm/regular" color="textMuted">
            a3s cannot confirm from here that a signal was produced. Check the cloud pipeline and the edge device to
            verify the outcome. If the asset is monitored, the signal should appear in Patriot as a test signal (code
            102).
          </Text>
        </View>

        {/* Side by side once there is room for two 280px cards, stacked below that. */}
        <View flexDirection="row" flexWrap="wrap" gap="3xl">
          {TEST_SIGNAL_ACTIONS.map((action) => (
            <TestSignalActionCard
              key={action.signalPath}
              action={action}
              disabled={trigger.isPending || cooldown.isCoolingDown(action.signalPath)}
              onPress={() => void fire(action.signalPath, action.title, action.confirmMessage)}
            />
          ))}
        </View>
      </View>
    </ScrollView>
  );
}
